import React, {useEffect, useState} from "react";
import {setUserPassword} from "../services/Request";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useGlobalContext} from "../context/GlobalState";
import {BaseInputPage} from "../components/BaseInputPage";

// MUI Imports
import {
    TextField,
    Box,
    InputAdornment,
    IconButton,
    Button,
} from "@mui/material";
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LoadingButton from "@mui/lab/LoadingButton";
import OtpDialog from "../components/OtpDialog";
import Cookies from "js-cookie";
import {Snackbar} from "../components/Snackbar";

export const SetPassword = () => {
    const {clearStorageAndCookies} = useGlobalContext();
    const [searchParams] = useSearchParams();
    const [userCreds, setUserCreds] = useState({
        password: "",
        passwordConfirm: "",
    });
    const [error, setError] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPass, setShowPass] = useState("password");
    const [success, setSuccess] = useState(false);
    const [otpDialogOpen, setOtpDialogOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        clearStorageAndCookies();
        Cookies.remove("resetToken")
        Cookies.set("resetToken", searchParams.get("token"), {
            secure:true
        });
        if(Cookies.get("resetToken")){
            setError(false);
        }
    }, [searchParams]);

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                navigate("/login");
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    const checkPassword = () => {
        if (userCreds.passwordConfirm.length < 8 || userCreds.password.length < 8) {
            setError(true);
            setErrorMsg("Password needs to be at least 8 characters in length.");
            return false;
        }
        if (userCreds.password !== userCreds.passwordConfirm) {
            setError(true);
            setErrorMsg("Passwords do not match.");
            return false;
        }
        setError(false);
        return true;
    };


    const OnSetPasswordSuccess = () => {
        setSuccess(true);
        setError(false);
        setLoading(false);
    };

    const OnSetPasswordFailure = (text) => {
        setLoading(false);
        Snackbar({message: text, variant: 'error'});
    };

    const handleOtpSubmit = async (otp) => {
        setLoading(true);
        return setUserPassword(userCreds.password, Cookies.get("resetToken"), otp);
    }
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (checkPassword()) {
            setLoading(true);
            try {
                const response = await setUserPassword(userCreds.password, Cookies.get("resetToken"), '');
                // Password can be changed but it needs a OTP token.
                if (response.status === 200 && response.data.retryRequired === true) {
                    setOtpDialogOpen(true);
                } 
                // Password can be changed, and no OTP auth is setup on the account.
                else if (response.status === 200){
                    OnSetPasswordSuccess();
                }
                // The reset request failed.
                else{
                    OnSetPasswordFailure("There was an unexpected response while setting the password. Please request a new password reset link.");
                }
            }
            catch {
                OnSetPasswordFailure("Your reset link may be expired. Please request a new password reset link.");
            }
        }
    };
    
    const handleToggle = (e) => {
        if (showPass === "password") {
            setShowPass("text");
        } else {
            setShowPass("password");
        }
    };
    return (
        <>
            <BaseInputPage
                pageTitle="Set Password"
                titleInfo="Set Password"
                isSuccessful={!error && success}
                successMsg="Password set successfully"
            >
                <Box
                    component="form"
                    noValidate
                    sx={{mt: 1}}
                    onSubmit={handleSubmit}
                >
                    <TextField
                        key="passwordInput"
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type={showPass}
                        id="password"
                        autoComplete="new-password"
                        value={userCreds.password}
                        onChange={(e) => setUserCreds({...userCreds, password: e.target.value})}
                        error={error}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleToggle}
                                                sx={{color: "primary.main"}}>
                                        {showPass === "password" ? <VisibilityOutlinedIcon/> :
                                            <VisibilityOffOutlinedIcon/>}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        key="passwordConfirmInput"
                        margin="normal"
                        required
                        fullWidth
                        name="passwordConfirm"
                        label="Confirm Password"
                        value={userCreds.passwordConfirm}
                        onChange={(e) =>
                            setUserCreds({...userCreds, passwordConfirm: e.target.value})
                        }
                        type={showPass}
                        id="passwordConfirm"
                        error={error}
                        helperText={errorMsg}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleToggle}
                                        sx={{color: "primary.main"}}>
                                        {showPass === "password" ? <VisibilityOutlinedIcon/> :
                                            <VisibilityOffOutlinedIcon/>}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <LoadingButton
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{my: 1}}
                        loading={loading}
                    >
                        Set Password
                    </LoadingButton>
                </Box>
                {/*Only show navigate button if successful setting password*/}
                {success && !error && (
                    <Button
                        variant="contained"
                        onClick={() => navigate('/login')}
                        sx={{mt: 2}}
                    >
                        Navigate to Login
                    </Button>
                )}
                <OtpDialog
                    open={otpDialogOpen}
                    showQrCode={false}
                    onClose={handleOnclose}
                    onSubmit={handleOtpSubmit}
                />
            </BaseInputPage>
        </>
    );

    function handleOnclose(response) {
        if(response?.status === 200){
            OnSetPasswordSuccess();
        }
        else{
            OnSetPasswordFailure("You must provide a two factor token to change your password.");
        }
        return setOtpDialogOpen(false);
    }
};
